// =========================================================================
// RigSentinel Merged Library — normalizer (Phase R2a, data ingest only).
//
// Single source of truth for parsing /lib/reference/merged_library_v1.json
// into a clean, partitioned runtime view. The on-disk JSON is preserved
// verbatim so an upstream regeneration is a one-shot overwrite; all cleanup
// happens here at load time.
//
// Responsibilities:
//   1. Trim & collapse internal whitespace on string values.
//   2. Lower-snake-case every `type` field.
//   3. Normalize `grade` ("J- 55" → "J-55", "100  ksi" → "100 ksi").
//   4. Alias `bits[*].iadc_code` from source `iadd_code` (preserve source).
//   5. Stamp every record with `__source`, `__loadedAt`, `__normalizedAt`.
//   6. Partition `equipment` into named buckets keyed by canonical `type`.
//
// NO UI consumed in R2a. Pickers wire up in R2b. Calculator formulas are not
// touched. Banned-verdict sweep is enforced in the test file.
// =========================================================================

import raw from "./merged_library_v1.json";
// Module 2 extension dataset — merged into the same equipment buckets so the
// /reference page (R2b) and the Module 2 Reference Libraries hub both see
// the same unified record set.
import extensionRaw from "../reflibs/extension_v1.json";

export const REFERENCE_DATASET = "rsc_seed_v1";
export const REFERENCE_DATASET_VERSION = "1.0.0";

const CANONICAL_EQUIPMENT_BUCKETS = [
  "drill_pipe",
  "drill_collar",
  "casing",
  "tubing",
  "bit",
  "heavy_weight_drill_pipe",
  "mud_motor",
  "rotary_steerable",
  "mwd_tool",
  "lwd_tool",
  "stabilizer",
  "crossover",
];

let _cached = null;

function _trimStr(s) {
  if (typeof s !== "string") return s;
  return s.trim().replace(/\s+/g, " ");
}

function _normType(s) {
  if (typeof s !== "string") return s;
  return s.trim().toLowerCase().replace(/\s+/g, "_");
}

function _normGrade(s) {
  if (typeof s !== "string") return s;
  // Strip whitespace inside hyphenated grades ("J- 55" → "J-55"), then trim+collapse.
  return s.replace(/-\s+/g, "-").trim().replace(/\s+/g, " ");
}

function _stampMeta(obj, loadedAt, normalizedAt) {
  obj.__source = REFERENCE_DATASET;
  obj.__loadedAt = loadedAt;
  obj.__normalizedAt = normalizedAt;
  return obj;
}

function _cleanRecord(rec, loadedAt, normalizedAt, opts = {}) {
  const out = {};
  for (const [k, v] of Object.entries(rec)) {
    if (typeof v === "string") {
      if (k === "type") out[k] = _normType(v);
      else if (k === "grade") out[k] = _normGrade(v);
      else out[k] = _trimStr(v);
    } else if (Array.isArray(v)) {
      out[k] = v.map((item) => {
        if (typeof item === "string") return _trimStr(item);
        if (item && typeof item === "object") return _cleanRecord(item, loadedAt, normalizedAt);
        return item;
      });
    } else if (v && typeof v === "object") {
      out[k] = _cleanRecord(v, loadedAt, normalizedAt, { suppressMeta: true });
    } else {
      out[k] = v;
    }
  }
  // Bit-record alias: source uses "iadd_code" — expose canonical "iadc_code"
  // alongside the original key for round-trip safety.
  if (opts.kind === "bit" && out.iadd_code && !out.iadc_code) {
    out.iadc_code = out.iadd_code;
  }
  if (!opts.suppressMeta && out.id !== undefined) {
    _stampMeta(out, loadedAt, normalizedAt);
  }
  return out;
}

function _partitionEquipment(list, loadedAt, normalizedAt) {
  const buckets = {};
  for (const b of CANONICAL_EQUIPMENT_BUCKETS) buckets[b] = [];
  for (const rec of list) {
    const kind = _normType(rec.type);
    const cleaned = _cleanRecord(rec, loadedAt, normalizedAt, { kind });
    if (buckets[kind]) buckets[kind].push(cleaned);
    else {
      if (!buckets.__unknown__) buckets.__unknown__ = [];
      buckets.__unknown__.push(cleaned);
    }
  }
  return buckets;
}

/**
 * Returns the normalized merged library as a cached singleton.
 * Idempotent: subsequent calls return the same in-memory object.
 */
export function loadMergedLibrary() {
  if (_cached) return _cached;
  const loadedAt = new Date().toISOString();
  const normalizedAt = new Date().toISOString();

  const equipment = _partitionEquipment(
    [...(raw.equipment || []), ...(extensionRaw.items || [])],
    loadedAt, normalizedAt,
  );

  const pumps = (raw.pumps || []).map((r) => _cleanRecord(r, loadedAt, normalizedAt));

  const drilling_fluids = {
    base_fluids: (raw.drilling_fluids?.base_fluids || []).map((r) =>
      _cleanRecord(r, loadedAt, normalizedAt)
    ),
    additives: (raw.drilling_fluids?.additives || []).map((r) =>
      _cleanRecord(r, loadedAt, normalizedAt)
    ),
    mud_properties_templates: (raw.drilling_fluids?.mud_properties_templates || []).map((r) =>
      _cleanRecord(r, loadedAt, normalizedAt)
    ),
  };

  const cement_slurries = (raw.cement_slurries || []).map((r) =>
    _cleanRecord(r, loadedAt, normalizedAt)
  );

  const bop_stacks = (raw.bop_stacks || []).map((r) =>
    _cleanRecord(r, loadedAt, normalizedAt)
  );

  const formation_templates = (raw.formation_templates || []).map((r) =>
    _cleanRecord(r, loadedAt, normalizedAt)
  );

  const constants = (raw.constants || []).map((r) =>
    _cleanRecord(r, loadedAt, normalizedAt)
  );

  // safety_thresholds is a nested object with a single "default" key. Stamp the
  // outer object so the picker UI in R2c can render provenance metadata, but
  // pass through the leaf numeric values untouched.
  const safety_thresholds = {
    default: { ...(raw.safety_thresholds?.default || {}) },
    __source: REFERENCE_DATASET,
    __loadedAt: loadedAt,
    __normalizedAt: normalizedAt,
  };

  _cached = {
    equipment,
    pumps,
    drilling_fluids,
    cement_slurries,
    bop_stacks,
    formation_templates,
    constants,
    safety_thresholds,
    __meta: {
      source: REFERENCE_DATASET,
      version: REFERENCE_DATASET_VERSION,
      loadedAt,
      normalizedAt,
    },
  };
  return _cached;
}

/**
 * Diagnostic snapshot of partition counts. Used by tests and the (future) R2c
 * Settings → Default Planning Factors card to display library provenance.
 */
export function getMergedLibraryStats() {
  const lib = loadMergedLibrary();
  const equipmentCounts = {};
  for (const b of CANONICAL_EQUIPMENT_BUCKETS) {
    equipmentCounts[b] = lib.equipment[b].length;
  }
  return {
    source: REFERENCE_DATASET,
    version: REFERENCE_DATASET_VERSION,
    loadedAt: lib.__meta.loadedAt,
    equipment_total: Object.values(equipmentCounts).reduce((a, b) => a + b, 0),
    equipment_by_kind: equipmentCounts,
    pumps: lib.pumps.length,
    base_fluids: lib.drilling_fluids.base_fluids.length,
    additives: lib.drilling_fluids.additives.length,
    mud_templates: lib.drilling_fluids.mud_properties_templates.length,
    cement_slurries: lib.cement_slurries.length,
    bop_stacks: lib.bop_stacks.length,
    formation_templates: lib.formation_templates.length,
    constants: lib.constants.length,
    safety_thresholds_keys: Object.keys(lib.safety_thresholds.default).length,
  };
}

// Exposed for testing only.
export const __internal__ = {
  _trimStr,
  _normType,
  _normGrade,
  CANONICAL_EQUIPMENT_BUCKETS,
};
